// @flow
import * as React from 'react';
import gql from 'graphql-tag';
import { css } from 'react-emotion';
import { connect } from 'react-redux';
import { Mutation } from 'react-apollo';
import { get } from 'lodash';
import { components, elements, colors } from '@peachjar/components';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { formatCamelCaseSentence } from '../../../../../onboarding/utils';

const { Modal, TextInput, Notifications } = components;
const { notifyError } = Notifications;
const { Paragraph } = elements.typography;
const { ButtonFlatLarge } = components.Buttons;
const { Spinner } = components.LoadingIndicators;

const SEND_ASSIGN_AGREEMENT_EMAIL_MUTATION = gql`
  mutation assignSchoolOnboardingAgreementToUser(
    $registrationId: String!
    $email: String!
    $fullName: String!
  ) {
    assignSchoolOnboardingAgreementToUser(
      registrationId: $registrationId
      email: $email
      fullName: $fullName
    ) {
      agreementStatus
    }
  }
`;

const validationSchema = Yup.object().shape({
  agreementAssigneeEmail: Yup.string()
    .email()
    .required('This field is required.'),
  agreementAssigneeName: Yup.string().required('This field is required.'),
});

type Props = {|
  +closeModal: () => void,
  +onNotifyError: (message: string) => void,
  +registrationId: string,
|};

class AssignAgreementModal extends React.Component<Props> {
  handleOnError = error => {
    const message = get(error, 'graphQLErrors[0].message') || error.message;
    this.props.closeModal();
    this.props.onNotifyError(message);
  };

  render() {
    const { closeModal, registrationId } = this.props;
    return (
      <Modal width="sm" onBackdropClick={closeModal}>
        <Formik
          initialValues={{
            agreementAssigneeEmail: '',
            agreementAssigneeName: '',
          }}
          validationSchema={validationSchema}
          render={({ values, errors, touched, handleChange, handleBlur }) => {
            const { agreementAssigneeEmail, agreementAssigneeName } = values;

            return (
              <form>
                <Modal.Header materialIcon="note" color={colors.gold}>
                  Assign Agreement
                </Modal.Header>

                <Modal.Body className="p-4">
                  <div>
                    <Paragraph>
                      Please enter in the name and email address of the person
                      you are assigning the agreement to.
                    </Paragraph>
                  </div>

                  <div className="p-3 pl-5 pr-5">
                    <div className="p-1">
                      <TextInput
                        error={
                          errors.agreementAssigneeName &&
                          touched.agreementAssigneeName
                        }
                        errorText={formatCamelCaseSentence(
                          errors.agreementAssigneeName
                        )}
                        inputProps={{
                          'data-testid': 'field-agreementAssigneeName',
                        }}
                        fullWidth
                        name="agreementAssigneeName"
                        label="Name"
                        value={agreementAssigneeName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                      />
                    </div>

                    <div className="p-1">
                      <TextInput
                        error={
                          errors.agreementAssigneeEmail &&
                          touched.agreementAssigneeEmail
                        }
                        errorText={formatCamelCaseSentence(
                          errors.agreementAssigneeEmail
                        )}
                        inputProps={{
                          'data-testid': 'field-agreementAssigneeEmail',
                        }}
                        fullWidth
                        name="agreementAssigneeEmail"
                        label="Email Address"
                        value={agreementAssigneeEmail}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                      />
                    </div>
                  </div>
                </Modal.Body>
                <Modal.Footer>
                  <ButtonFlatLarge
                    data-testid="click-closeAssignDocusignModalButton"
                    onClick={closeModal}
                    className="pj-margin-right--narrow"
                  >
                    Cancel
                  </ButtonFlatLarge>
                  <Mutation
                    mutation={SEND_ASSIGN_AGREEMENT_EMAIL_MUTATION}
                    onCompleted={closeModal}
                    onError={this.handleOnError}
                  >
                    {(sendAssignAgreementEmail, { loading, error }) => {
                      const variables = {
                        registrationId,
                        email: agreementAssigneeEmail,
                        fullName: agreementAssigneeName,
                      };

                      return (
                        <ButtonFlatLarge
                          data-testid="submit-sendAssignDocusignButton"
                          type="submit"
                          onClick={() =>
                            sendAssignAgreementEmail({
                              variables,
                            })
                          }
                          disabled={
                            !agreementAssigneeName ||
                            !agreementAssigneeEmail ||
                            !!error ||
                            !!loading
                          }
                        >
                          {loading && <Spinner />}
                          <span>Assign</span>
                        </ButtonFlatLarge>
                      );
                    }}
                  </Mutation>
                </Modal.Footer>
              </form>
            );
          }}
        />
      </Modal>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  onNotifyError: message => dispatch(notifyError(message)),
});

export default connect(
  null,
  mapDispatchToProps
)(AssignAgreementModal);



// WEBPACK FOOTER //
// ./src/Onboarding/Registration/onboarding/containers/Payment/components/modals/AssignDocusignModal.jsx